import { Link, useNavigate } from "@/lib/router-compat";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Compass, Home, LifeBuoy, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { useSEO } from "@/hooks/useSEO";

const NotFound = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Unknown URLs must never be indexed or present themselves as real pages.
  useSEO({
    title: "Page Not Found | Amorette",
    description: "This Amorette page could not be found. Explore launch markets, how Amorette works, or contact the concierge team.",
    noindex: true,
  });

  const destinations = [
    { to: "/", label: t("common.return_to_home", "Return to Home"), icon: Home },
    { to: "/learn-more", label: "How Amorette Works", icon: Compass },
    { to: "/cities", label: "Cities", icon: MapPin },
    { to: "/contact", label: "Contact Concierge", icon: LifeBuoy },
  ];

  return (
    <main className="min-h-screen bg-background flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-lg text-center">
        <div className="flex justify-center mb-8">
          <Logo />
        </div>

        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">404</p>
        <h1 className="text-3xl md:text-4xl font-serif text-foreground mb-4">
          {t("common.page_not_found", "Page not found")}
        </h1>
        <p className="text-muted-foreground mb-10">
          This page has moved or never existed. Let us guide you back to the experience.
        </p>

        <div className="grid gap-3 sm:grid-cols-2 text-left">
          {destinations.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm text-card-foreground transition-colors hover:border-primary/50 hover:bg-accent"
            >
              <Icon className="h-4 w-4 text-primary shrink-0" />
              <span>{label}</span>
            </Link>
          ))}
        </div>

        <Button variant="ghost" className="mt-8" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Go back
        </Button>
      </div>
    </main>
  );
};

export default NotFound;
